import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import classNames from 'classnames/bind';
import { useRouter } from 'next/router';
import {
  useMutation,
  OperationVariables,
  ApolloQueryResult,
} from '@apollo/client';
import { Mutation } from '@apollo/client/react/components';
import { getIconNameByStoredValue } from 'helpers/spaceHelpers';
import HighlightContainer from 'components/HighlightArea/HighlightContainer';
import { H1, Caption } from 'koba/components/Typography';
import Icon from 'koba/components/Icon';
import { ButtonWithIconLeft } from 'koba/components/Button';
import PostActions from 'components/PostActions';
import ReactionButton from 'components/ReactionButton';
import { getReactors } from 'helpers/reactionHelpers';
import { Loader, toaster, Button } from '@thinkific/toga-react';
import { goToSpaces } from 'utils/routerHelpers';
import { makeMentionsUserFriendly } from 'utils/mentions';
import UserAvatar from 'components/UserAvatar';
import {
  BREAKPOINT_MD,
  COMMENT_FORM_ID,
  EVENT_POST_TYPES,
  POSTS_PER_PAGE,
  SUCCESS_TOAST,
  REACTION_TYPES,
  NUMBER_OF_REACTIONS,
} from 'utils/constants';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';

import { POST_LIST_QUERY } from 'schema/Post/queries';
import { DANGER_TYPE } from 'koba/components/InlineActions/constants';
import { useCommunityContext } from 'components/CommunityContext';
import { isModerator } from 'utils/userHelpers';
import { relativeFormattedDateTime } from 'helpers/dateHelpers';
import {
  DELETE_POST_MUTATION,
  FOLLOW_POST_MUTATION,
  UNFOLLOW_POST_MUTATION,
  PIN_POST_MUTATION,
  UNPIN_POST_MUTATION,
} from 'schema/Post/mutations';
import { useWindowSize } from 'hooks/useWindowSize';
import { rawId } from 'utils/rawId';
import { genericToastAlert } from 'utils/toastHelpers';

import { TFunction } from 'next-i18next';
import { MentionedUsers } from 'types/mentions';
import { PostListUser } from 'types/user';
import { Post as PostType, PostAction } from 'types/post';
import { PostList } from 'schema/Post/__generated__/PostList';
import UserDetail from '../UserDetail';

import styles from './Post.module.scss';

const cx = classNames.bind(styles);

// Define outside of the component so all post components can share the same imports
let ExpandableTextArea;
let MentionsTextArea;
let CommentListSingular;
let AddCommentModal;
let CommentList;
let AddCommentContainer;
let AttachmentDisplayer;
let EditPostModal;

interface PostProps {
  additionalClassName?: string | null;
  communityWrapperRef?: React.RefObject<HTMLDivElement>;
  content: string;
  goToPostListPage?(): void;
  isPostPage?: boolean;
  lastCommentRef?: React.RefObject<HTMLDivElement>;
  mentionedUsers?: MentionedUsers;
  post: PostType;
  postReactionCount: number;
  refetchPostList?: (
    variables?: Partial<OperationVariables>
  ) => Promise<ApolloQueryResult<PostList>>;
  shouldShowTagOnPost?: boolean;
  t: TFunction;
  time: string;
  totalCommentCount: number;
  author: PostListUser;
  onPostChange?(): void;
}

const Post: React.FC<PostProps> = ({
  additionalClassName = '',
  content,
  mentionedUsers,
  author,
  post,
  time,
  t,
  isPostPage = false,
  refetchPostList,
  totalCommentCount,
  postReactionCount,
  goToPostListPage,
  lastCommentRef = {},
  communityWrapperRef = {},
  shouldShowTagOnPost = false,
  onPostChange,
}) => {
  const { community, currentUser, trackEvent } = useCommunityContext();

  const firstAttachment = post?.postAttachments?.[0];

  const refetchQueries = [
    {
      query: POST_LIST_QUERY,
      variables: {
        communityId: community.id,
        numOfPosts: POSTS_PER_PAGE,
        filter: {
          following: true,
        },
        postOrderField: undefined,
        numberOfReactions: NUMBER_OF_REACTIONS,
      },
    },
  ];

  const [unfollowPost, { loading: unfollowLoading }] = useMutation(
    UNFOLLOW_POST_MUTATION,
    {
      refetchQueries,
    }
  );
  const [followPost, { loading: followLoading }] = useMutation(
    FOLLOW_POST_MUTATION,
    {
      refetchQueries,
    }
  );
  const [pinPost, { loading: pinLoading }] = useMutation(PIN_POST_MUTATION);
  const [unpinPost, { loading: unpinLoading }] = useMutation(
    UNPIN_POST_MUTATION
  );
  const { width } = useWindowSize();
  const isMobile = width < BREAKPOINT_MD;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [shouldClampContent, setShouldClampContent] = useState<boolean>(
    (content?.length || 0) > 480
  );

  const router = useRouter();
  const commentIdFromURL = router && router.query && router.query.comment;
  const [commentId, setCommentId] = useState(commentIdFromURL);

  useEffect(() => {
    setCommentId(commentIdFromURL);
  }, [commentIdFromURL]);

  // Load all the things
  if (isPostPage) {
    if (!ExpandableTextArea) {
      ExpandableTextArea = dynamic(
        () => import('components/ExpandableTextArea'),
        {
          loading: () => (
            <Loader accessibilityLabel={t('common-loading', 'Loading')} />
          ),
        }
      );
    }
    if (!MentionsTextArea) {
      MentionsTextArea = dynamic(
        () => import('components/Mentions/MentionsTextArea'),
        {
          loading: () => (
            <Loader accessibilityLabel={t('common-loading', 'Loading')} />
          ),
        }
      );
    }
    if (commentId && !CommentListSingular) {
      CommentListSingular = dynamic(
        () => import('components/CommentListSingular'),
        {
          loading: () => (
            <Loader accessibilityLabel={t('common-loading', 'Loading')} />
          ),
        }
      );
    }
    if (!AddCommentModal) {
      AddCommentModal = dynamic(() => import('components/AddCommentModal'), {
        loading: () => (
          <Loader accessibilityLabel={t('common-loading', 'Loading')} />
        ),
      });
    }
    if (!CommentList) {
      CommentList = dynamic(() => import('components/CommentList'), {
        loading: () => (
          <Loader accessibilityLabel={t('common-loading', 'Loading')} />
        ),
      });
    }
    if (!AddCommentContainer) {
      AddCommentContainer = dynamic(
        () => import('components/AddCommentContainer'),
        {
          loading: () => (
            <Loader accessibilityLabel={t('common-loading', 'Loading')} />
          ),
        }
      );
    }
  }
  if (!AttachmentDisplayer) {
    AttachmentDisplayer = dynamic(
      () => import('components/AttachmentDisplayer'),
      {
        loading: () => (
          <Loader accessibilityLabel={t('common-loading', 'Loading')} />
        ),
      }
    );
  }

  const trackPostEvent = (event) =>
    trackEvent(event, {
      [TrackingProperty.ATTACHMENT]: firstAttachment?.attachmentType,
      [TrackingProperty.PINNED_POST]: !!post.pinnedAt,
      [TrackingProperty.POST_ID]: rawId(post.id),
      [TrackingProperty.POST_ID_ENCODED]: post.id,
      [TrackingProperty.POST_TYPE]: EVENT_POST_TYPES.POST,
    });

  const goToPost = () => {
    router
      .push(
        {
          pathname: '/[communityId]/post/[postId]',
          query: {
            communityId: community.id,
            postId: post.id,
            canUseBrowserBack: true,
          },
        },
        `/communities/${community.id}/post/${post.id}`
      )
      .then(() => window.scrollTo(0, 0));
  };

  const handleClickOnSpaceBadge = (event, filter, name) => {
    event.preventDefault();
    event.stopPropagation();

    trackEvent(TrackingEvent.SPACE_CLICKED, {
      [TrackingProperty.SPACE_ID]: rawId(filter),
      [TrackingProperty.SPACE_NAME]: name,
    });

    goToSpaces(router, filter, community.id, true);
  };

  const handleDeletePost = (event) => {
    document.body.style.overflow = 'hidden';
    setIsOpen(true);
    if (event) event.stopPropagation();
  };

  const closeModal = () => {
    document.body.style.overflow = 'visible';
    setIsOpen(false);
  };

  const handleEditPost = (event) => {
    document.body.style.overflow = 'hidden';
    setIsEditModalOpen(true);
    if (event) event.stopPropagation();
  };

  const closeEditPostModal = () => {
    document.body.style.overflow = 'visible';
    setIsEditModalOpen(false);
  };

  const handleConfirmDelete = (mutation) => async () => {
    try {
      const { data: deletePostData, errors: deletePostError } = await mutation({
        variables: { id: post.id },
        refetchQueries: isPostPage ? [] : ['PostList', 'CommunitySpace'],
        update() {
          closeModal();
        },
      });
      trackPostEvent(TrackingEvent.DELETE_POST);

      const userErrors = deletePostData?.deletePost?.userErrors;
      if (!userErrors?.length && !deletePostError?.length) {
        toaster.notice(t(SUCCESS_TOAST.DELETE_POST));
      } else {
        genericToastAlert();
      }

      onPostChange?.();

      if (isPostPage) {
        goToPostListPage?.();
      }
    } catch {
      genericToastAlert();
    }
  };

  const currentUserIsModerator = isModerator(currentUser.roles);
  const userCanEditPost =
    currentUserIsModerator || (currentUser.id && currentUser.id === author.id);

  const postRef = React.useRef<HTMLDivElement>(null);
  const creationDateTime = relativeFormattedDateTime(
    time,
    currentUser?.locale,
    t
  );

  const renderButtonForMobile = (openCommentModal, value) => {
    const displayValue = makeMentionsUserFriendly(value);

    const placeholderValue = t(
      'Type @ to mention another member in your comment'
    );

    return (
      <button
        className={cx('post-comment', { 'has-value': !!value })}
        type="button"
        onClick={openCommentModal}
      >
        {
          // Makes mentions in the placeholder text on the input that triggers the modal open more friendly.
        }
        {displayValue || placeholderValue}
      </button>
    );
  };

  const onCommentSubmitted = () => {
    onPostChange?.();
    if (commentId) {
      goToPost();
    }
  };

  const relevantMentions =
    author.id !== currentUser.id
      ? [
          {
            id: author.id,
            avatarUrl: author.avatarUrl,
            display: author.fullName,
          },
        ]
      : [];

  const getCommentBox = (inputProps) => {
    if (window.innerWidth > BREAKPOINT_MD) {
      return (
        <ExpandableTextArea
          id={COMMENT_FORM_ID}
          label={t('components-post-add_comment', 'Add comment...')}
          labelClass="sr-only"
          mentionsComponent={
            <MentionsTextArea
              additionalClassName={additionalClassName}
              id={COMMENT_FORM_ID}
              isModal={inputProps.isModal}
              isPostForm={inputProps.isPostForm}
              placeholder={t(
                'Type @ to mention another member in your comment'
              )}
              relevantMentions={relevantMentions}
              textAreaClassName="mentions__text-area"
              value={inputProps.value}
              onChange={inputProps.handleMentionsChange}
            />
          }
          parentPostRef={postRef}
          s3UploadPath={community.s3UploadPath}
          isAttachmentButtonsEnabled
          isAttachmentRenderedInPortal
          {...inputProps}
        />
      );
    }

    return (
      <AddCommentModal
        additionalClassName="add-comment__modal"
        relevantMentions={relevantMentions}
        {...inputProps}
        renderButton={renderButtonForMobile}
      />
    );
  };
  // eslint-disable-next-line no-shadow
  const commentInput = (inputProps, currentUser) => (
    <div className={cx('comment-input__wrapper')}>
      <UserAvatar
        avatarUrl={currentUser.avatarUrl}
        className={cx('comment-input__avatar-container')}
        size="sm"
        srcSet={`${currentUser.avatarUrl}?width=40 1x, ${currentUser.avatarUrl}?width=40&dpr=2 2x, ${currentUser.avatarUrl}?width=40&dpr=3 3x`}
        userId={currentUser.id}
        username={currentUser.fullName}
        shouldOpenProfile
      />

      {getCommentBox(inputProps)}
    </div>
  );

  const getCommentSection = () => {
    let commentList;

    if (commentId) {
      commentList = (
        <CommentListSingular
          commentId={commentId}
          communityWrapperRef={communityWrapperRef}
          lastCommentRef={lastCommentRef}
          parentPostId={post.id}
          parentPostRef={postRef}
          t={t}
        />
      );
    } else {
      commentList = (
        <CommentList
          communityWrapperRef={communityWrapperRef}
          lastCommentRef={lastCommentRef}
          parentPostId={post.id}
          parentPostRef={postRef}
          t={t}
          onPostChange={onPostChange}
        />
      );
    }

    return (
      <div className={cx('comment-container')}>
        {commentList}
        <AddCommentContainer
          communityWrapperRef={communityWrapperRef}
          lastCommentRef={lastCommentRef}
          parentPostId={post.id}
          renderChildren={commentInput}
          t={t}
          onCommentSubmitted={onCommentSubmitted}
        />
      </div>
    );
  };

  const renderEditPostModal = () => {
    if (!EditPostModal) {
      EditPostModal = dynamic(() => import('components/EditPostModal'), {
        loading: () => (
          <Loader accessibilityLabel={t('common-loading', 'Loading')} />
        ),
      });
    }

    return (
      <EditPostModal
        closeModal={closeEditPostModal}
        communityId={community.id}
        content={content}
        hasAutoFocus={isMobile}
        post={post}
        primaryColor={community.primaryColor}
        s3UploadPath={community.s3UploadPath}
        trackEvent={trackEvent}
      />
    );
  };
  const renderConfirmDeleteModal = (mutate, loading) => {
    const ConfirmDeleteModal = dynamic(() =>
      import('components/ConfirmDeleteModal')
    );

    return (
      <span>
        <ConfirmDeleteModal
          closeAction={closeModal}
          confirmDeleteBody={t(
            'components-post-confirm_delete_post',
            'Are you sure you want to delete this post? You cannot undo this action.'
          )}
          confirmDeleteTitle={t('Delete post')}
          deleteAction={handleConfirmDelete(mutate)}
          isConfirmModalOpen={isOpen}
          isLoading={loading}
        />
      </span>
    );
  };

  const commentList = isPostPage && getCommentSection();

  const postActionsList: PostAction[] = [];

  if (!isPostPage) {
    if (post.isFollowing && !unfollowLoading) {
      postActionsList.push({
        name: 'unfollow',
        text: `${t('Unfollow')}`,
        onClick: () => {
          unfollowPost({
            variables: { id: post.id, numberOfReactions: NUMBER_OF_REACTIONS },
          }).then(() => {
            onPostChange?.();
            trackPostEvent(TrackingEvent.UNFOLLOW);
          });
        },
      });
    } else if (!followLoading) {
      postActionsList.push({
        name: 'follow',
        text: t('Follow'),
        onClick: () => {
          followPost({
            variables: { id: post.id, numberOfReactions: NUMBER_OF_REACTIONS },
          }).then(() => {
            onPostChange?.();
            trackPostEvent(TrackingEvent.FOLLOW);
          });
        },
      });
    }
  }

  if (userCanEditPost) {
    postActionsList.push({
      name: 'edit',
      text: t('common-edit', 'Edit'),
      onClick: handleEditPost,
    });
  }

  if (currentUserIsModerator) {
    if (post.pinnedAt && !unpinLoading) {
      postActionsList.push({
        name: 'unpin',
        text: t('Unpin'),
        onClick: () =>
          unpinPost({
            variables: { id: post.id, numberOfReactions: NUMBER_OF_REACTIONS },
          }).then(() => {
            onPostChange?.();
            trackPostEvent(TrackingEvent.UNPIN);
            refetchPostList?.({ shouldShowLoading: true });
          }),
      });
    } else if (!pinLoading) {
      postActionsList.push({
        name: 'pin',
        text: t('Pin'),
        onClick: () =>
          pinPost({
            variables: { id: post.id, numberOfReactions: NUMBER_OF_REACTIONS },
          }).then(() => {
            onPostChange?.();
            trackPostEvent(TrackingEvent.PIN);
            refetchPostList?.({ shouldShowLoading: true });
          }),
      });
    }

    postActionsList.push({
      name: 'delete',
      text: (
        <div className={cx('post__detail-action')}>
          <Icon className={cx('post__detail-action__icon')} name="trash-can" />
          <span>{t('Delete post')}</span>
        </div>
      ),
      title: t('Delete post'),
      onClick: handleDeletePost,
      type: DANGER_TYPE,
    });
  }

  const hasAttachment = post.postAttachments && post.postAttachments[0];

  const reactionButtonContent = (
    <ReactionButton
      hasReacted={post.hasReacted}
      id={post.id}
      isReactingTo="post"
      postReactionCount={postReactionCount}
      reactionType={REACTION_TYPES.LIKE_POST}
      reactors={getReactors(post)}
      t={t}
    />
  );

  const getAttachmentDisplayer = () => (
    <AttachmentDisplayer
      attachment={firstAttachment}
      isPostPage={isPostPage}
      t={t}
    />
  );

  const getPostContent = () => {
    const handleExpandCollapse = (e: Event) => {
      if (!shouldClampContent && !isPostPage) {
        window.scrollTo(0, postRef.current?.offsetTop || 0);
      }

      e.preventDefault();
      e.stopPropagation();
      setShouldClampContent(!shouldClampContent);
    };

    const readContentButton = content.length > 480 && (
      <Button
        appearance="accessible-link"
        className={cx('post__content--button')}
        data-qa="read-post__button"
        onClick={handleExpandCollapse}
      >
        {shouldClampContent ? t('Read more') : t('Read less')}
      </Button>
    );

    return (
      <span
        className={cx(
          'post__content',
          hasAttachment ? 'post__content--attachment' : ''
        )}
        data-qa="post__content"
      >
        <div
          className={cx(
            'post__content__text',
            shouldClampContent ? 'post__content__text--clamped' : ''
          )}
        >
          <HighlightContainer
            content={content}
            mentionedUsers={mentionedUsers}
          />
        </div>
        {readContentButton}
        {hasAttachment && getAttachmentDisplayer()}
      </span>
    );
  };

  const postContent = (
    <div
      className={cx('post__detail', {
        'is-spaces-enabled': shouldShowTagOnPost, // we should try and avoid coupling feature names to classes
      })}
      id={post.id}
      role="article"
    >
      <div
        className={cx('post__body', {
          'is-spaces-enabled': shouldShowTagOnPost,
        })}
      >
        <div
          className={cx('post__detail-header', {
            'is-following': !isPostPage && post.isFollowing,
          })}
        >
          <UserDetail
            author={author}
            editedAt={post.editedAt}
            time={creationDateTime || ''}
          />
          <div className={cx('post__detail-actions')}>
            {!isPostPage && post.isFollowing && (
              <Caption className={cx('post__detail-actions__follow-tag')}>
                {t('Following')}
              </Caption>
            )}
            {post.pinnedAt && (
              <Icon
                ariaLabel={t('This post is pinned to the top of the list')}
                className={cx('post__detail-actions__pin-icon')}
                name="pin"
              />
            )}
            {postActionsList.length > 0 && (
              <PostActions
                actionsList={postActionsList}
                isMobile={isMobile}
                t={t}
              />
            )}
          </div>
        </div>
        <H1 className={cx('post__title')} data-qa="main-post-title__text">
          {post.title}
        </H1>
        {getPostContent()}
      </div>
      {!isPostPage ? (
        <div
          className={cx('post__footer', {
            'is-spaces-enabled': shouldShowTagOnPost,
          })}
        >
          <div>
            {reactionButtonContent}
            {/* @ts-ignore */}
            <ButtonWithIconLeft
              appearance="knockout"
              data-qa="main-comments_text"
              size="small"
              onClick={goToPost}
            >
              <Icon name="discussions" />
              <span>
                {t('{{count}} comment', { count: totalCommentCount })}
              </span>
            </ButtonWithIconLeft>
          </div>
          {shouldShowTagOnPost && post.communitySpace && (
            <a
              aria-label={t('Go to {{space}}', {
                space: post.communitySpace?.name,
              })}
              className={cx('post__space')}
              data-qa="space-badge-link"
              href={`/communities/${community.id}/spaces/${post.communitySpace?.id}`}
              title={post.communitySpace?.name || ''}
              onClick={(event) =>
                handleClickOnSpaceBadge(
                  event,
                  post.communitySpace?.id,
                  post.communitySpace?.name
                )
              }
            >
              <Icon
                className={cx('post__space__icon')}
                data-qa="post__space__icon"
                name={getIconNameByStoredValue(
                  post.communitySpace?.iconName || ''
                )}
              />
              {post.communitySpace?.name}
            </a>
          )}
        </div>
      ) : (
        <div className={cx('post__info')}>
          {reactionButtonContent}
          <div className={cx('total-comments')} data-qa="main-comments_text">
            <Icon name="discussions" />
            <span>{t('{{count}} comment', { count: totalCommentCount })}</span>
          </div>
          <hr className={cx('post__separator')} />
        </div>
      )}
      {commentList}
    </div>
  );

  return (
    <div className={cx('post')}>
      {isPostPage ? (
        <div
          className={cx('post__container', 'post__container--full')}
          ref={postRef}
        >
          {postContent}
        </div>
      ) : (
        <div
          aria-label={`${t('Read full post')}: ${post.title}`}
          className={cx('post__container')}
          ref={postRef}
          role="button"
          tabIndex={0}
          onClick={goToPost}
          onKeyPress={(e) => {
            if (e.key !== 'Enter' && e.key !== ' ') {
              goToPost();
            }
          }}
        >
          {postContent}
        </div>
      )}

      {isEditModalOpen && renderEditPostModal()}

      {currentUserIsModerator && isOpen && (
        <Mutation mutation={DELETE_POST_MUTATION}>
          {(mutate, { loading }) => renderConfirmDeleteModal(mutate, loading)}
        </Mutation>
      )}
    </div>
  );
};

export default Post;
